// Every route declares what an asset-class switch does to it (`meta.assetClassScope`):
//   keep — the page means the same in every asset class: same path, query dropped;
//   list — the page shows one resource that belongs to one asset class: the switch goes to
//          `meta.fallback` instead (the id would be a 404 in the other asset class). These are also
//          the "resource routes" that an un-prefixed entry URL resolves to IBIPs (see assetClassEntry);
//   home — the page is not tied to an asset class: the switch goes to `/`.
// `meta.requires` names a permission that must hold in the target asset class (see `can`); when it
// does not, the switch goes to `/`. A test keeps every route declaring its scope.
const EFFICIENCY_TEST = 'canViewEfficiencyTest'
const SCORING_MONITORING = 'canViewScoringMonitoring'
// Not tied to an asset class: whoever may validate may use the back office pages.
const VALIDATE = 'canValidate'

export const routes = [
  {
    path: '*',
    redirect: '/error/404',
    meta: { assetClassScope: 'home' },
  },
  {
    path: '/error/:code',
    name: 'http-error',
    meta: { assetClassScope: 'home' },
    props: true,
    component: () => import(/* webpackChunkName: "HTTPError" */ '../views/ErrorService/HTTPError'),
  },
  {
    // Where a route that needs a permission the asset class lacks sends the user (see assetClassAccess).
    path: '/assetClassUnavailable',
    name: 'asset-class-unavailable',
    meta: { assetClassScope: 'home' },
    props: (route) => ({ from: route.query.from }),
    component: () =>
      import(/* webpackChunkName: "asset-class-unavailable" */ '../views/ErrorService/AssetClassUnavailable'),
  },
  {
    path: '/',
    name: 'index',
    meta: { assetClassScope: 'keep' },
    component: () => import(/* webpackChunkName: "search" */ '../views/Search'),
  },
  {
    path: '/configurations',
    meta: { assetClassScope: 'keep' },
    component: () => import(/* webpackChunkName: "configurations" */ '../views/Configurations'),
    children: [
      {
        path: '/',
        redirect: '/configurations/notifications',
      },
      {
        path: '/configurations/notifications',
        component: () =>
          import(/* webpackChunkName: "conf-notifications" */ '../views/ConfigurationPanels/Notifications'),
      },
      {
        path: '/configurations/buildInformation',
        component: () =>
          import(/* webpackChunkName: "conf-software-version" */ '../views/ConfigurationPanels/BuildInformation'),
      },
      {
        // Back office of the issuers of the asset class in the URL prefix.
        path: '/configurations/issuers',
        meta: { requires: VALIDATE, forbiddenRedirect: '/configurations' },
        component: () => import(/* webpackChunkName: "conf-issuers" */ '../views/ConfigurationPanels/Issuers'),
      },
      {
        // Back office of the long-running operations of the asset class in the URL prefix, of every company.
        path: '/configurations/operations',
        meta: { requires: VALIDATE, forbiddenRedirect: '/configurations' },
        component: () => import(/* webpackChunkName: "conf-operations" */ '../views/ConfigurationPanels/Operations'),
      },
    ],
  },
  {
    // Catalog-driven Followed page.
    path: '/followed',
    name: 'followed',
    meta: { assetClassScope: 'keep' },
    component: () => import(/* webpackChunkName: "followed" */ '../views/Followed'),
  },
  {
    // Catalog-driven search page.
    path: '/search',
    name: 'search',
    meta: { assetClassScope: 'keep' },
    component: () => import(/* webpackChunkName: "search" */ '../views/Search'),
  },
  {
    // Config-driven Product Detail page.
    path: '/productDetails/:productName/:issuer/:referenceDate?/:investmentOptionName?',
    name: 'productDetails',
    meta: { assetClassScope: 'list', fallback: '/search' },
    props: true,
    component: () => import(/* webpackChunkName: "product-details" */ '../views/ProductDetails.vue'),
  },
  {
    path: '/profileEditor',
    name: 'profileEditor',
    meta: { assetClassScope: 'keep' },
    component: () => import(/* webpackChunkName: "profiles-editor" */ '../views/ProfilesEditor'),
  },
  {
    // Catalog-native "Compare to market".
    path: '/marketComparison/:productName/:issuer/:referenceDate/:investmentOptionName?',
    name: 'marketComparison',
    meta: { assetClassScope: 'list', fallback: '/search' },
    props: true,
    component: () => import(/* webpackChunkName: "market-comparison" */ '../views/MarketComparison'),
  },
  {
    path: '/indicatorsHistory/:productName/:issuer/:referenceDate',
    name: 'indicatorsHistory',
    meta: { assetClassScope: 'list', fallback: '/search' },
    props: true,
    component: () => import(/* webpackChunkName: "indicators-history" */ '../views/IndicatorsHistory'),
  },
  {
    path: '/companyProducts',
    name: 'companyProducts',
    meta: { assetClassScope: 'keep' },
    component: () => import(/* webpackChunkName: "product-in-test-search" */ '../views/CompanyProducts'),
  },
  {
    path: '/productsInTestImport',
    name: 'productsInTestImport',
    meta: { assetClassScope: 'keep' },
    component: () => import(/* webpackChunkName: "product-in-test-import" */ '../views/Pit/ProductsInTestImport'),
  },
  {
    path: '/scoringProfilesList',
    name: 'scoringProfilesList',
    meta: { assetClassScope: 'keep' },
    component: () =>
      import(/* webpackChunkName: "scoring-profiles-list" */ '../views/ScoringProfiles/ScoringProfilesList'),
  },
  {
    path: '/scoringProfilesModelsList',
    name: 'scoringProfilesModelsList',
    meta: { assetClassScope: 'keep' },
    component: () =>
      import(
        /* webpackChunkName: "scoring-profiles-models-list" */ '../views/ScoringProfiles/ScoringProfilesModelsList'
      ),
  },
  {
    // Catalog-driven create flow.
    path: '/scoringProfilesCreate',
    name: 'scoringProfilesCreate',
    meta: { assetClassScope: 'keep' },
    component: () =>
      import(
        /* webpackChunkName: "scoring-profiles-create" */ '../views/ScoringProfiles/ScoringProfilesCreate'
      ),
  },
  {
    // Catalog-driven edit flow.
    path: '/scoringProfilesEdit/:id',
    name: 'scoringProfilesEdit',
    meta: { assetClassScope: 'list', fallback: '/scoringProfilesList' },
    props: true,
    component: () =>
      import(/* webpackChunkName: "scoring-profiles-edit" */ '../views/ScoringProfiles/ScoringProfilesEdit'),
  },
  {
    // Insights page fed by the catalog compatibility engine.
    path: '/scoringProfilesDetails/:scoringProfileId',
    name: 'scoringProfilesDetails',
    meta: { assetClassScope: 'list', fallback: '/scoringProfilesList' },
    props: true,
    component: () =>
      import(/* webpackChunkName: "scoring-profiles-details" */ '../views/ScoringProfiles/ScoringProfilesDetails'),
  },
  {
    path: '/ScoringProfilesModelCreate',
    name: 'scoringProfilesModelCreate',
    meta: { assetClassScope: 'keep' },
    props: true,
    component: () =>
      import(
        /* webpackChunkName: "scoring-profiles-model-create" */ '../views/ScoringProfiles/ScoringProfilesModelCreate'
      ),
  },
  {
    path: '/ScoringProfilesModelEdit/:id',
    name: 'scoringProfilesModelEdit',
    meta: { assetClassScope: 'list', fallback: '/scoringProfilesModelsList' },
    props: true,
    component: () =>
      import(/* webpackChunkName: "scoring-profiles-model-edit" */ '../views/ScoringProfiles/ScoringProfilesModelEdit'),
  },
  {
    path: '/monitoringList',
    name: 'monitoringList',
    meta: { assetClassScope: 'keep', requires: SCORING_MONITORING },
    component: () => import(/* webpackChunkName: "monitoring-list" */ '../views/ScoringProfiles/MonitoringList'),
  },
  {
    path: '/monitoringHistory',
    name: 'monitoringHistory',
    meta: { assetClassScope: 'keep', requires: SCORING_MONITORING },
    component: () => import(/* webpackChunkName: "monitoring-history" */ '../views/ScoringProfiles/MonitoringHistory'),
  },
  {
    path: '/efficiencyTestsList',
    name: 'efficiencyTestsList',
    meta: { assetClassScope: 'keep', requires: EFFICIENCY_TEST },
    component: () =>
      import(/* webpackChunkName: "efficiency-tests-list" */ '../views/EfficiencyTests/EfficiencyTestsList'),
  },
  {
    path: '/efficiencyTestsCreate',
    name: 'efficiencyTestsCreate',
    meta: { assetClassScope: 'keep', requires: EFFICIENCY_TEST },
    component: () =>
      import(
        /* webpackChunkName: "efficiency-tests-create-new" */ '../views/EfficiencyTests/EfficiencyTestsCreate'
      ),
  },
  {
    path: '/efficiencyTestsEdit/:id',
    name: 'efficiencyTestsEdit',
    meta: { assetClassScope: 'list', fallback: '/search', requires: EFFICIENCY_TEST },
    props: true,
    component: () =>
      import(/* webpackChunkName: "efficiency-tests-edit-new" */ '../views/EfficiencyTests/EfficiencyTestsEdit.vue'),
  },
  {
    path: '/efficiencyTestsDetails/:scoringProfileId',
    name: 'efficiencyTestsDetails',
    meta: { assetClassScope: 'list', fallback: '/search', requires: EFFICIENCY_TEST },
    props: true,
    component: () =>
      import(/* webpackChunkName: "efficiency-tests-details" */ '../views/EfficiencyTests/EfficiencyTestsDetails'),
  },
]
